import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationManager } from 'react-notifications';
import BtButton from '../bt-button/bt-button';
import StyledCheckbox from '../../components/shared/StyledCheckbox/StyledCheckbox';
import { TabPage } from '../../components/shared/PageWithTabs/TabPage';
import { TabsType } from '../../components/shared/PageWithTabs/PageWithTabs';
import { ServiceSupplierProfileReminders } from '../../services/service-supplier-profile-reminders';

type Props = {
  tabs: TabsType,
}

type Supplier = {
  id: string,
  orgId: string | null,
  name: string,
  lastReminderSentAt: string | null,
}

type LackingResult = {
  total: number,
  suppliers: Supplier[],
}

export const SupplierProfileRemindersTab: FC<Props> = ({ tabs }) => {
  const [t] = useTranslation('supplierProfileReminders');
  const [loading, setLoading] = useState(false);
  const [lacking, setLacking] = useState<LackingResult | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const effectiveSendCount = useMemo(() => {
    if (!lacking) return 0;
    return selectedIds.size > 0 ? selectedIds.size : lacking.total;
  }, [lacking, selectedIds]);

  const allSelected = !!lacking && lacking.total > 0 && selectedIds.size === lacking.total;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleOne = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (!lacking) return;
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(lacking.suppliers.map(s => s.id)));
  };

  const handleClick = async () => {
    setLoading(true);
    try {
      if (!lacking) {
        const result: LackingResult = await ServiceSupplierProfileReminders.getLacking();
        setLacking(result);
        setSelectedIds(new Set());
      } else if (lacking.total > 0) {
        const idsToSend = selectedIds.size > 0 ? Array.from(selectedIds) : undefined;
        const result = await ServiceSupplierProfileReminders.sendReminders(idsToSend);
        NotificationManager.success(
          result.emailsFailed
            ? t(
              'sendResult.withFailures',
              'Skickade {{sent}} e-post till {{suppliers}} leverantörer, {{failed}} misslyckades.',
              { sent: result.emailsSent, suppliers: result.suppliersProcessed, failed: result.emailsFailed },
            )
            : t(
              'sendResult.success',
              'Skickade {{sent}} e-post till {{suppliers}} leverantörer.',
              { sent: result.emailsSent, suppliers: result.suppliersProcessed },
            ),
        );
        setLacking(null);
        setSelectedIds(new Set());
      }
    } catch (err: any) {
      console.error(err);
      NotificationManager.error(
        err?.response?.data?.message
          || err?.message
          || t('errors.requestFailed', 'Begäran misslyckades'),
      );
    } finally {
      setLoading(false);
    }
  };

  const buttonLabel = (() => {
    if (!lacking) return t('button.fetch', 'Hämta leverantörer som saknar arbetsområden/regioner');
    if (lacking.total === 0) return t('button.noneToRemind', 'Inga leverantörer att påminna');
    if (selectedIds.size > 0) {
      return t(
        'button.sendSelected',
        'Skicka påminnelse till {{count}} valda leverantör',
        { count: effectiveSendCount },
      );
    }
    return t(
      'button.sendAll',
      'Skicka påminnelse till alla {{count}} leverantör',
      { count: effectiveSendCount },
    );
  })();

  return (
    <TabPage tabs={tabs} title={t('title', 'Påminnelser - leverantörsprofil')}>
      <div style={{ marginTop: 24, maxWidth: 720 }}>
        <p style={{ marginBottom: 16, color: 'var(--gray-600)' }}>
          {t(
            'description',
            'Skickar e-postpåminnelser till administratörer på leverantörsorganisationer vars leverantörsprofil saknar arbetsområden eller regioner.',
          )}
        </p>

        <BtButton
          onClick={handleClick}
          disabled={loading || (lacking !== null && lacking.total === 0)}
          loaderShow={loading}
        >
          {buttonLabel}
        </BtButton>

        {lacking && (
          <div style={{ marginTop: 24 }}>
            <h3 style={{ marginBottom: 12 }}>
              {t(
                'list.heading',
                '{{count}} leverantörsprofil saknar arbetsområden eller regioner',
                { count: lacking.total },
              )}
            </h3>
            {lacking.total > 0 && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <StyledCheckbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                  />
                  <span style={{ fontSize: 13, color: 'var(--gray-700)' }}>
                    {selectedIds.size > 0
                      ? t('list.selectedCount', '{{count}} valda', { count: selectedIds.size })
                      : t('list.selectAll', 'Välj alla (lämna tomt för att skicka till alla)')}
                  </span>
                </div>
                <ul style={{ paddingLeft: 0, listStyle: 'none', maxHeight: 480, overflowY: 'auto' }}>
                  {lacking.suppliers.map(s => (
                    <li
                      key={s.id}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}
                    >
                      <StyledCheckbox
                        checked={selectedIds.has(s.id)}
                        onChange={() => toggleOne(s.id)}
                      />
                      <span>{s.name}</span>
                      <span style={{ color: 'var(--gray-600)', fontSize: 13 }}>
                        {s.lastReminderSentAt
                          ? t(
                            'list.lastReminded',
                            '(senast påmind {{date}})',
                            { date: new Date(s.lastReminderSentAt).toLocaleDateString('sv-SE') },
                          )
                          : t('list.neverReminded', '(aldrig påmind)')}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>
    </TabPage>
  );
};

export default SupplierProfileRemindersTab;
